import { apiFetch, apiFetchResult, toQuery } from "./client";

export type ListMeta = {
  page?: number;
  pageSize?: number;
  total?: number;
  totalPages?: number;
  language?: string;
};

export type ListParams = Record<string, string | number | boolean | undefined | null>;

async function list<T>(path: string, params: ListParams = {}) {
  return apiFetchResult<{ items: T[] }>(`${path}${toQuery(params)}`);
}

export const destinationsApi = {
  list: (params: ListParams = {}) => list<Record<string, unknown>>("/api/destinations", params),
  bySlug: (slug: string, language = "en") =>
    apiFetch<Record<string, unknown>>(
      `/api/destinations/${encodeURIComponent(slug)}${toQuery({ language })}`,
    ),
};

export const attractionsApi = {
  list: (params: ListParams = {}) => list<Record<string, unknown>>("/api/attractions", params),
  bySlug: (slug: string, language = "en") =>
    apiFetch<Record<string, unknown>>(
      `/api/attractions/${encodeURIComponent(slug)}${toQuery({ language })}`,
    ),
};

export const hotelsApi = {
  list: (params: ListParams = {}) => list<Record<string, unknown>>("/api/hotels", params),
  bySlug: (slug: string, language = "en") =>
    apiFetch<Record<string, unknown>>(
      `/api/hotels/${encodeURIComponent(slug)}${toQuery({ language })}`,
    ),
};

export const restaurantsApi = {
  list: (params: ListParams = {}) => list<Record<string, unknown>>("/api/restaurants", params),
  bySlug: (slug: string, language = "en") =>
    apiFetch<Record<string, unknown>>(
      `/api/restaurants/${encodeURIComponent(slug)}${toQuery({ language })}`,
    ),
};

export const experiencesApi = {
  list: (params: ListParams = {}) => list<Record<string, unknown>>("/api/experiences", params),
  bySlug: (slug: string, language = "en") =>
    apiFetch<Record<string, unknown>>(
      `/api/experiences/${encodeURIComponent(slug)}${toQuery({ language })}`,
    ),
};

export const eventsApi = {
  list: (params: ListParams = {}) => list<Record<string, unknown>>("/api/events", params),
  bySlug: (slug: string, language = "en") =>
    apiFetch<Record<string, unknown>>(
      `/api/events/${encodeURIComponent(slug)}${toQuery({ language })}`,
    ),
};

export const travelGuidesApi = {
  list: (params: ListParams = {}) => list<Record<string, unknown>>("/api/travel-guides", params),
  bySlug: (slug: string, language = "en") =>
    apiFetch<Record<string, unknown>>(
      `/api/travel-guides/${encodeURIComponent(slug)}${toQuery({ language })}`,
    ),
};

export const searchApi = {
  query: (params: ListParams) => list<Record<string, unknown>>("/api/search", params),
};

export const nearbyApi = {
  query: (params: ListParams) => list<Record<string, unknown>>("/api/nearby", params),
};

export const recommendationsApi = {
  generate: (body: Record<string, unknown>, language = "en") =>
    apiFetch<{ engine: string; destinations: Record<string, unknown>[] }>(
      `/api/recommendations${toQuery({ language })}`,
      { method: "POST", body: JSON.stringify(body) },
    ),
};

export const itinerariesApi = {
  list: () => apiFetch<{ items: Record<string, unknown>[] }>("/api/itineraries"),
  create: (body: Record<string, unknown>) =>
    apiFetch<{ id: string }>("/api/itineraries", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  get: (id: string) =>
    apiFetch<{
      itinerary: Record<string, unknown>;
      days: Record<string, unknown>[];
      items: Record<string, unknown>[];
    }>(`/api/itineraries/${encodeURIComponent(id)}`),
  remove: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/api/itineraries/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
};

export type FavoriteEntityType =
  | "destination"
  | "attraction"
  | "hotel"
  | "restaurant"
  | "experience"
  | "event";

export type FavoriteRow = {
  id: string;
  user_id?: string;
  entity_type: FavoriteEntityType | string;
  entity_id: string;
  created_at?: string;
  name?: string;
  slug?: string | null;
  city?: string | null;
  isDemo?: boolean;
};

export const favoritesApi = {
  list: (language = "en") =>
    apiFetch<{ items: FavoriteRow[] }>(`/api/favorites${toQuery({ language })}`),
  add: (entityType: FavoriteEntityType | string, entityId: string) =>
    apiFetch<{ id: string; alreadyExists?: boolean }>("/api/favorites", {
      method: "POST",
      body: JSON.stringify({ entityType, entityId }),
    }),
  remove: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/api/favorites/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  removeByEntity: (entityType: FavoriteEntityType | string, entityId: string) =>
    apiFetch<{ deleted: boolean }>(
      `/api/favorites/by/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
      { method: "DELETE" },
    ),
};

export type ReviewRow = {
  id: string;
  user_id?: string;
  rating: number;
  body?: string;
  created_at?: string;
};

export const reviewsApi = {
  list: (entityType: FavoriteEntityType | string, entityId: string) =>
    apiFetch<{ items: ReviewRow[] }>(
      `/api/reviews/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
    ),
  create: (
    entityType: FavoriteEntityType | string,
    entityId: string,
    body: { rating: number; body?: string },
  ) =>
    apiFetch<{ id: string; moderationStatus: string }>(
      `/api/reviews/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
      { method: "POST", body: JSON.stringify(body) },
    ),
};

export type AuthUser = {
  id: string;
  email: string;
  name: string;
  role: string;
};

export const authApi = {
  register: (body: { email: string; password: string; name?: string }) =>
    apiFetch<{ user: AuthUser; token: string }>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  login: (body: { email: string; password: string }) =>
    apiFetch<{ user: AuthUser; token: string }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  me: () => apiFetch<{ user: AuthUser }>("/api/auth/me"),
  logout: () =>
    apiFetch<{ loggedOut: boolean }>("/api/auth/logout", {
      method: "POST",
      body: "{}",
    }),
};

export type AdminEntity =
  | "destinations"
  | "attractions"
  | "hotels"
  | "restaurants"
  | "experiences"
  | "events"
  | "travel-guides";

export const adminApi = {
  stats: () =>
    apiFetch<{
      destinations: number;
      attractions: number;
      hotels: number;
      reviewsPending: number;
      users: number;
    }>("/api/admin/stats"),
  listContent: (entity: AdminEntity, params: ListParams = {}) =>
    apiFetch<{ items: Record<string, unknown>[]; role?: string }>(
      `/api/admin/content/${entity}${toQuery(params)}`,
    ),
  getContent: (entity: AdminEntity, id: string) =>
    apiFetch<{
      item: Record<string, unknown>;
      translations: Record<string, unknown>[];
      role?: string;
    }>(`/api/admin/content/${entity}/${encodeURIComponent(id)}`),
  createContent: (entity: AdminEntity, body: Record<string, unknown>) =>
    apiFetch<{ id: string; slug: string }>(`/api/admin/content/${entity}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateContent: (entity: AdminEntity, id: string, body: Record<string, unknown>) =>
    apiFetch<{ id: string; updated: boolean }>(
      `/api/admin/content/${entity}/${encodeURIComponent(id)}`,
      { method: "PUT", body: JSON.stringify(body) },
    ),
  publish: (entity: AdminEntity, id: string, status: string) =>
    apiFetch<{ id: string; status: string }>(
      `/api/admin/${entity}/${encodeURIComponent(id)}/publish`,
      { method: "PATCH", body: JSON.stringify({ status }) },
    ),
  feature: (entity: AdminEntity, id: string, featured: boolean) =>
    apiFetch<{ id: string; featured: boolean }>(
      `/api/admin/content/${entity}/${encodeURIComponent(id)}/feature`,
      { method: "PATCH", body: JSON.stringify({ featured }) },
    ),
  deleteContent: (entity: AdminEntity, id: string) =>
    apiFetch<{ deleted: boolean }>(
      `/api/admin/content/${entity}/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    ),
  listReviews: (status = "pending") =>
    apiFetch<{ items: Record<string, unknown>[] }>(
      `/api/admin/reviews${toQuery({ status })}`,
    ),
  moderateReview: (id: string, status: string) =>
    apiFetch<{ id: string; status: string }>(
      `/api/admin/reviews/${encodeURIComponent(id)}/moderate`,
      { method: "POST", body: JSON.stringify({ status }) },
    ),
  listBusinesses: (params: ListParams = {}) =>
    apiFetch<{ items: Record<string, unknown>[] }>(
      `/api/admin/businesses${toQuery(params)}`,
    ),
  listUsers: () =>
    apiFetch<{ items: Record<string, unknown>[]; role?: string }>("/api/admin/users"),

  // Tourism Intelligence
  researchGovernorates: () =>
    apiFetch<{ items: Record<string, unknown>[] }>("/api/admin/research/geo/governorates"),
  researchCities: (governorateId: string) =>
    apiFetch<{ items: Record<string, unknown>[] }>(
      `/api/admin/research/geo/cities${toQuery({ governorate_id: governorateId })}`,
    ),
  researchAreas: (cityId: string) =>
    apiFetch<{ items: Record<string, unknown>[] }>(
      `/api/admin/research/geo/areas${toQuery({ city_id: cityId })}`,
    ),
  listResearchJobs: (limit = 30) =>
    apiFetch<{ items: Record<string, unknown>[] }>(
      `/api/admin/research/jobs${toQuery({ limit })}`,
    ),
  getResearchJob: (id: string) =>
    apiFetch<{
      job: Record<string, unknown>;
      stages: Record<string, unknown>[];
      stats: Record<string, unknown>;
    }>(`/api/admin/research/jobs/${encodeURIComponent(id)}`),
  startResearchJob: (body: Record<string, unknown>) =>
    apiFetch<{ job: Record<string, unknown> }>("/api/admin/research/jobs", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  advanceResearchJob: (id: string) =>
    apiFetch<{ job: Record<string, unknown>; stageCompleted: string; done: boolean }>(
      `/api/admin/research/jobs/${encodeURIComponent(id)}/advance`,
      { method: "POST", body: "{}" },
    ),
  cancelResearchJob: (id: string) =>
    apiFetch<{ id: string; status: string }>(
      `/api/admin/research/jobs/${encodeURIComponent(id)}/cancel`,
      { method: "POST", body: "{}" },
    ),
  listResearchCandidates: (
    jobId: string,
    params: { entity_type?: string; review_status?: string; limit?: number } = {},
  ) =>
    apiFetch<{ items: Record<string, unknown>[] }>(
      `/api/admin/research/jobs/${encodeURIComponent(jobId)}/candidates${toQuery(params)}`,
    ),
  getResearchCandidate: (id: string) =>
    apiFetch<{
      candidate: Record<string, unknown>;
      facts: Record<string, unknown>[];
      images: Record<string, unknown>[];
      links: Record<string, unknown>[];
    }>(`/api/admin/research/candidates/${encodeURIComponent(id)}`),
  updateResearchCandidate: (id: string, body: Record<string, unknown>) =>
    apiFetch<{ candidate: Record<string, unknown> }>(
      `/api/admin/research/candidates/${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),
  approveResearchCandidate: (id: string, publish = false) =>
    apiFetch<{ entityType: string; entityId: string; slug: string }>(
      `/api/admin/research/candidates/${encodeURIComponent(id)}/approve`,
      { method: "POST", body: JSON.stringify({ publish }) },
    ),
  rejectResearchCandidate: (id: string) =>
    apiFetch<{ id: string; review_status: string }>(
      `/api/admin/research/candidates/${encodeURIComponent(id)}/reject`,
      { method: "POST", body: "{}" },
    ),
  mergeResearchCandidates: (
    id: string,
    body: {
      link_id: string;
      resolution: "merge" | "keep_separate" | "reject";
      keep_candidate_id?: string;
    },
  ) =>
    apiFetch<{ link_id: string; resolution: string }>(
      `/api/admin/research/candidates/${encodeURIComponent(id)}/merge`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  researchEngineStatus: () =>
    apiFetch<{
      status: string;
      batchId?: string | null;
      areasTotal?: number;
      areasCompleted?: number;
      areasRunning?: number;
      areasQueued?: number;
      candidatesDiscovered?: number;
      reviewable?: number;
      evidenceBacked?: number;
      drafts?: number;
      published?: number;
      currentAreas?: Record<string, unknown>[];
      geography?: Record<string, unknown>[];
      errors?: string[];
      safety: { autoPublish: boolean; clearIsDemo: boolean; endsAt: string };
      notice?: string;
      // legacy fields if present
      engine?: string;
      run?: Record<string, unknown> | null;
      queue?: Record<string, unknown>[];
    }>("/api/admin/research/engine/status"),
  researchEngineStart: (body: Record<string, unknown>) =>
    apiFetch<{
      batchId?: string;
      runId: string;
      enqueued?: number;
      status: Record<string, unknown>;
      notice: string;
    }>("/api/admin/research/engine/start", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  researchEnginePause: (runId?: string) =>
    apiFetch<{ id: string; status: string }>("/api/admin/research/engine/pause", {
      method: "POST",
      body: JSON.stringify(runId ? { run_id: runId } : {}),
    }),
  researchEngineResume: (runId?: string) =>
    apiFetch<{ id: string; status: string; enqueued?: number }>(
      "/api/admin/research/engine/resume",
      {
        method: "POST",
        body: JSON.stringify(runId ? { run_id: runId } : {}),
      },
    ),
  researchEngineStop: (runId?: string) =>
    apiFetch<{ id: string; status: string }>("/api/admin/research/engine/stop", {
      method: "POST",
      body: JSON.stringify(runId ? { run_id: runId } : {}),
    }),
  researchEngineTick: () =>
    apiFetch<{ tick: Record<string, unknown>; status: Record<string, unknown> }>(
      "/api/admin/research/engine/tick",
      { method: "POST", body: "{}" },
    ),
  researchBatches: () =>
    apiFetch<{ items: Record<string, unknown>[] }>("/api/admin/research/engine/batches"),
};

export {
  apiFetch,
  apiFetchResult,
  getHealth,
  getAuthToken,
  setAuthToken,
  AUTH_TOKEN_KEY,
  API_BASE_URL,
} from "./client";

export {
  normalizeCatalogItem,
  normalizeList,
  pathForEntityType,
  imageOf,
  type CatalogItem,
} from "./normalize";
